import {
  TabbedDrawer,
  Button,
  Icon,
  H5,
  TabbedDrawerHeader,
  TabbedDrawerTitle,
  TabbedDrawerTab,
  TabbedDrawerCTA,
} from "@sellar-io/components";
import {
  calculateCollabageddonPricing,
  formatCurrency,
} from "@sellar-io/common";
import { useNavigate } from "react-router";
import { OrderSummaryPrice } from "../../components";
import { DietaryDisplayList } from "../../components/dietaryDisplayList/DietaryDisplayList";
import { useBuyerProduct } from "../../hooks/api/v2/product/useBuyerProduct";

import { COLLABAGEDDON_CHECKOUT } from "../../constants/routes";
import { CollabageddonProduct } from "./CollabageddonProductCard";

enum BasketDrawerTabs {
  BASKET,
}

export const BasketDrawer = ({
  active,
  setActive,
  selectedProducts,
  onRemoveAll,
}: {
  active: boolean;
  setActive: (value: boolean) => void;
  selectedProducts: CollabageddonProduct[];
  onRemoveAll: () => void;
}) => {
  const navigate = useNavigate();

  // Calculate totals
  const { subTotal, taxAmount, total } =
    calculateCollabageddonPricing(selectedProducts);

  const handleCheckout = () => {
    navigate(COLLABAGEDDON_CHECKOUT);
  };

  const handleRemoveAll = () => {
    onRemoveAll();
    setActive(false);
  };

  return (
    <TabbedDrawer
      active={active}
      setActive={setActive}
      initTab={BasketDrawerTabs.BASKET}
    >
      <TabbedDrawerHeader className={"bg-white"}>
        <TabbedDrawerTitle
          title="Your basket"
          subtitle={`Review your selected beers (${selectedProducts.length})`}
          customIcon={
            <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-lg bg-zinc-100 p-3">
              <div className="flex h-8 w-8 items-center justify-center">
                <div className="relative h-6 w-6 overflow-hidden">
                  <Icon icon="CART" />
                </div>
              </div>
            </div>
          }
        />
      </TabbedDrawerHeader>

      <TabbedDrawerTab tab={BasketDrawerTabs.BASKET} className={"bg-zinc-100"}>
        <div className="flex flex-col gap-2 p-6">
          {selectedProducts.map((product) => (
            <BasketProduct key={product.id} collabageddonProduct={product} />
          ))}
        </div>
      </TabbedDrawerTab>

      <TabbedDrawerCTA className={"bg-zinc-100"}>
        <OrderSummaryPrice
          subTotal={formatCurrency(subTotal)}
          total={formatCurrency(total)}
          tax={{
            name: "VAT",
            percentage: "20",
            total: formatCurrency(taxAmount),
          }}
          hideDelivery={true}
          discount={undefined}
        />
        <div className="flex w-full flex-col gap-3">
          <Button className="w-full" onClick={handleCheckout}>
            GO TO CHECKOUT
          </Button>
          <Button
            className={"w-full text-red-500"}
            color="tertiary"
            onClick={handleRemoveAll}
          >
            REMOVE ALL
          </Button>
        </div>
      </TabbedDrawerCTA>
    </TabbedDrawer>
  );
};

const BasketProduct = ({
  collabageddonProduct,
}: {
  collabageddonProduct: CollabageddonProduct;
}) => {
  const productQuery = useBuyerProduct(collabageddonProduct.id);
  const product = productQuery.data;

  return (
    <div className="inline-flex items-center justify-start gap-4 self-stretch overflow-hidden p-4">
      <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg">
        {product?.Parent?.imageUrl ? (
          <img
            src={product.Parent.imageUrl || product.imageUrl || ""}
            alt={product.name || ""}
            className="h-full w-full object-cover"
          />
        ) : null}
      </div>

      <div className="inline-flex flex-1 flex-col items-start justify-start">
        <div className="flex flex-col">
          <H5>{product?.name}</H5>
          <DietaryDisplayList
            dietaryRequirements={{
              VG: Boolean(product?.Parent?.vegan),
              GF: Boolean(product?.Parent?.glutenFree),
              LF: Boolean(product?.Parent?.lactoseFree),
            }}
            mode="text"
          />
        </div>
        <div className="inline-flex items-center justify-center">
          <div className="text-sm leading-tight font-normal text-zinc-700">
            1× 30L KeyKeg
          </div>
        </div>
      </div>

      <div className="leading-loose font-semibold text-zinc-800">
        {formatCurrency(collabageddonProduct.price)}
      </div>
    </div>
  );
};
